import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { ago, money, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'
import { useEnumOptions } from '../lib/enums'

interface BuyerRow {
  id: string; code: string; name: string; buyerType: string; kraPin: string | null
  creditState: string; creditLimit: number; paymentTermsDays: number
  corridorName: string | null; corridorId: string | null
  exposure: number; fillRatePct: number; rejectionRatePct: number
  lastOrderAt: string | null; orders30d: number; kg30d: number; revenue30d: number
}

interface Corridor {
  id: string; code: string; name: string; city: string | null
  targetDrops: number; isOpenForSales: boolean; buyerCount: number
}

const CREDIT_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  on_terms: 'ok', cash_only: 'info', watch: 'warn', on_hold: 'crit', blocked: 'crit',
}

export function Buyers() {
  // Who a buyer is, is sales's to write. What credit they get is not — that is the
  // finance call, and the server keeps the two apart.
  const canSell = session.has('admin', 'sales', 'ops')
  const canCredit = session.has('admin', 'finance')

  const buyers = useApi<BuyerRow[]>('/buyers?pageSize=200')
  const corridors = useApi<Corridor[]>('/reference/corridors')

  const [creating, setCreating] = useState(false)
  const [crediting, setCrediting] = useState<BuyerRow | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  const rows = buyers.data ?? []
  const exposure = rows.reduce((s, b) => s + Number(b.exposure ?? 0), 0)
  const onTerms = rows.filter((b) => b.creditState === 'on_terms').length
  const closedCorridors = corridors.data?.filter((c) => !c.isOpenForSales) ?? []

  return (
    <main className="page">
      <div className="page-head">
        <h1>Buyers</h1>
        <p>
          Who you sell to, what they owe, and whether they pay on time. A buyer who pays in a week
          at a smaller margin is worth more to you than one who pays in two months at a bigger one.
        </p>
      </div>

      <div className="stack">
        {banner && <Banner tone="info" title={banner}>{null}</Banner>}

        {closedCorridors.length > 0 && (
          <Banner tone="info" title="Some areas are not taking new buyers">
            {closedCorridors.map((c) => c.name).join(', ')} — until each hits its density target.
            Adding a buyer outside a served corridor makes every drop on it more expensive, so the
            gate is enforced on create rather than left to judgement.
          </Banner>
        )}

        <Panel
          title="Delivery areas"
          meta="judge each area on its own, not on an average across all of them"
        >
          {corridors.loading && <Loading rows={3} />}
          {corridors.data && (
            <div className="panel-body mini-row">
              {corridors.data.map((c) => (
                <StatMini
                  key={c.id}
                  label={c.name}
                  value={`${c.buyerCount} buyers`}
                  note={
                    c.isOpenForSales
                      ? `target ${c.targetDrops} drops/van-day · open`
                      : `target ${c.targetDrops} drops/van-day · closed to sales`
                  }
                />
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Buyer book"
          meta={canSell && <button className="btn" onClick={() => setCreating(true)}>New buyer</button>}
          footer={
            <>
              <StatMini label="Buyers" value={rows.length} note={`${onTerms} on terms`} />
              <StatMini label="Owed to you" value={money(exposure, { compact: true })} />
              <StatMini
                label="Orders filled in full"
                value={
                  rows.length > 0
                    ? `${(rows.reduce((s, b) => s + Number(b.fillRatePct ?? 0), 0) / rows.length).toFixed(1)}%`
                    : '—'
                }
              />
              <StatMini label="Sold in 30 days" value={money(rows.reduce((s, b) => s + Number(b.revenue30d ?? 0), 0), { compact: true })} />
            </>
          }
        >
          {buyers.error && <ErrorNote error={buyers.error} onRetry={buyers.reload} />}
          {buyers.loading && <Loading rows={5} />}
          {buyers.data?.length === 0 && (
            <Empty
              title="No buyers yet"
              action={canSell ? <button className="btn" onClick={() => setCreating(true)}>New buyer</button> : undefined}
            >
              Add one from an open corridor.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th><th>Buyer</th><th>Type</th><th>Area</th>
                    <th>Credit</th><th className="n">Terms</th><th className="n">Owes you</th>
                    <th className="n">Filled in full</th><th className="n">Rejections</th><th className="n">Last order</th>
                    {canCredit && <th style={{ width: 90 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => {
                    const overLimit = Number(b.exposure) > Number(b.creditLimit) && Number(b.creditLimit) > 0
                    return (
                      <tr key={b.id} className={overLimit || b.creditState === 'on_hold' ? 'attention' : ''}>
                        <td className="code strong">{b.code}</td>
                        <td className="truncate">
                          <Link
                            to={`/console/orders?buyerId=${b.id}&buyerName=${encodeURIComponent(b.name)}`}
                            className="row-link"
                            title={`What ${b.name} has ordered`}
                          >
                            {b.name}
                          </Link>
                          {!b.kraPin && <> <Pill tone="warn" dot={false}>no KRA PIN</Pill></>}
                        </td>
                        <td>{titleCase(b.buyerType)}</td>
                        <td className="truncate faint">{b.corridorName ?? '—'}</td>
                        <td><Pill tone={CREDIT_TONE[b.creditState] ?? 'neutral'}>{titleCase(b.creditState)}</Pill></td>
                        <td className="n">{b.paymentTermsDays} d</td>
                        <td className="n">
                          {money(b.exposure, { currency: false })}
                          {overLimit && <> <Pill tone="crit" dot={false}>over limit</Pill></>}
                        </td>
                        <td className="n">{Number(b.fillRatePct ?? 0).toFixed(1)}%</td>
                        <td className="n">
                          <span className={Number(b.rejectionRatePct) > 5 ? 'crit-t' : ''}>
                            {Number(b.rejectionRatePct ?? 0).toFixed(1)}%
                          </span>
                        </td>
                        <td className="n faint">{b.lastOrderAt ? ago(b.lastOrderAt) : 'never'}</td>
                        {canCredit && (
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn quiet" style={smallBtn} onClick={() => setCrediting(b)}>
                              Credit
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Banner tone="warn" title="Watch who sends produce back">
          A buyer who rejects far more than everyone else is either getting worse produce than the
          rest, or trying it on. Either way somebody should go and see. The rejections column above
          is where you notice first.
        </Banner>
      </div>

      {creating && (
        <NewBuyer
          corridors={corridors.data ?? []}
          onClose={() => setCreating(false)}
          onDone={(name) => {
            setCreating(false)
            setBanner(`${name} added. Set their credit before the first order goes out on terms.`)
            buyers.reload()
          }}
        />
      )}

      {crediting && (
        <SetCredit
          buyer={crediting}
          onClose={() => setCrediting(null)}
          onDone={() => {
            setBanner(`${crediting.name}'s credit updated.`)
            setCrediting(null)
            buyers.reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- new buyer
function NewBuyer({ corridors, onClose, onDone }: {
  corridors: Corridor[]
  onClose: () => void
  onDone: (name: string) => void
}) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const buyerTypes = useEnumOptions('buyer_type', { ngo: 'NGO' })
  const open = corridors.filter((c) => c.isOpenForSales)

  const fields: FormFieldDef[] = [
    { name: 'name', label: 'Business name', required: true },
    {
      name: 'buyerType', label: 'Type', type: 'select', required: true, half: true,
      options: buyerTypes,
    },
    {
      name: 'corridorId', label: 'Corridor', type: 'select', half: true,
      options: open.map((c) => ({ value: c.id, label: `${c.name}${c.city ? ` · ${c.city}` : ''}` })),
      hint: open.length < corridors.length ? 'Closed corridors are not listed' : undefined,
    },
    {
      name: 'kraPin', label: 'KRA PIN', half: true,
      hint: 'Needed before you can send them a tax invoice',
    },
    { name: 'countyCode', label: 'County code', half: true },
    { name: 'address', label: 'Delivery address', type: 'textarea' },
    { name: 'windowStart', label: 'Delivery window opens', type: 'text', half: true, placeholder: '06:00' },
    { name: 'windowEnd', label: 'and closes', type: 'text', half: true, placeholder: '10:00' },
    {
      name: 'decisionMakerRole', label: 'Who signs for deliveries', half: true,
      hint: 'The person who actually receives it, not whoever signed the contract',
    },
  ]

  async function submit() {
    const res = await api.post<{ name: string }>('/buyers', {
      name: values.name,
      buyerType: values.buyerType,
      corridorId: values.corridorId || null,
      kraPin: values.kraPin || null,
      countyCode: values.countyCode || null,
      address: values.address || null,
      lat: null, lng: null,
      windowStart: values.windowStart || null,
      windowEnd: values.windowEnd || null,
      decisionMakerRole: values.decisionMakerRole || null,
    })
    onDone(res.name ?? String(values.name))
  }

  return (
    <FormDrawer
      kind="New" title="Buyer" submitLabel="Add buyer"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
    >
      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        A new buyer starts paying cash on delivery. Letting them pay later is a separate
        decision — use the Credit button in the list.
      </p>
    </FormDrawer>
  )
}

// ---------------------------------------------------------------- credit
function SetCredit({ buyer, onClose, onDone }: {
  buyer: BuyerRow
  onClose: () => void
  onDone: () => void
}) {
  const creditStates = useEnumOptions('credit_state', {
    cash_only: 'Cash only - pays on delivery',
    on_terms: 'On terms - invoice and wait',
    watch: 'Watch - on terms, but paying late',
    on_hold: 'On hold - no new dispatches',
    blocked: 'Blocked - no new orders',
  })
  const [values, setValues] = useState<Record<string, unknown>>({
    creditState: buyer.creditState,
    creditLimit: String(buyer.creditLimit ?? 0),
    paymentTermsDays: String(buyer.paymentTermsDays ?? 0),
  })
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const fields: FormFieldDef[] = [
    {
      name: 'creditState', label: 'Credit state', type: 'select', required: true,
      options: creditStates,
    },
    { name: 'creditLimit', label: 'Credit limit', type: 'money', required: true, half: true },
    { name: 'paymentTermsDays', label: 'Terms', type: 'number', required: true, half: true, suffix: 'days' },
    {
      name: 'note', label: 'Why', type: 'textarea',
      hint: 'Whoever handles this account next will read this.',
    },
  ]

  const exposure = Number(buyer.exposure ?? 0)
  const limit = Number(values.creditLimit) || 0

  async function submit() {
    await api.put(`/buyers/${buyer.id}/credit`, {
      creditState: values.creditState,
      creditLimit: Number(values.creditLimit),
      paymentTermsDays: Number(values.paymentTermsDays),
      note: values.note || null,
    })
    onDone()
  }

  return (
    <FormDrawer
      kind="Credit" title={buyer.name} submitLabel="Save credit"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={<>owes <b>{money(exposure)}</b> today</>}
    >
      {limit > 0 && exposure > limit && (
        <Banner tone="crit" title="They already owe more than this limit">
          They owe {money(exposure)} and you are setting a {money(limit)} limit. This does not
          get the money back — it stops you sending them anything more.
        </Banner>
      )}
    </FormDrawer>
  )
}

const smallBtn = { minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' } as const
